import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { del, get, post } from "../../../../utils/helpers/api";
import { useNavigate } from "react-router-dom";
import { Badge } from "reactstrap";
import IconButton from "../../../../components/Buttons/IconButton";
import { BsTrash3 } from "react-icons/bs";
import { LuTruck, LuPackageCheck, LuBan } from "react-icons/lu";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import DefaultPagination from "../../../../components/Pagination";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { useTranslation } from "react-i18next";
import AgGridTable from "../../../../components/Tables/AgGrid";
import { toast } from "react-toastify";

const STATUS_COLORS = {
  draft: 'secondary',
  in_transit: 'warning',
  received: 'success',
  cancelled: 'danger',
};

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      {data.status === 'draft' && (
        <IconButton
          onClick={(e) => { e.stopPropagation(); context.act('dispatch', data.id); }}
          size="sm" outline color="primary" title={context.t('transfers.dispatch')}
        >
          <LuTruck />
        </IconButton>
      )}
      {data.status === 'in_transit' && (
        <IconButton
          onClick={(e) => { e.stopPropagation(); context.act('receive', data.id); }}
          size="sm" outline color="success" title={context.t('transfers.receive')}
        >
          <LuPackageCheck />
        </IconButton>
      )}
      {(data.status === 'draft' || data.status === 'in_transit') && (
        <IconButton
          onClick={(e) => { e.stopPropagation(); context.act('cancel', data.id); }}
          size="sm" outline color="warning" title={context.t('transfers.cancel')}
        >
          <LuBan />
        </IconButton>
      )}
      {/* Only a draft has moved nothing, so only a draft is safe to delete. */}
      {data.status === 'draft' && (
        <IconButton
          onClick={(e) => { e.stopPropagation(); context.setToDelete(data.id); }}
          size="sm" outline color="danger"
        >
          <BsTrash3 />
        </IconButton>
      )}
    </div>
  );
}

function TransfersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [toDelete, setToDelete] = useState(null);

  const getTransfers = useCallback(async () => {
    setLoading(true);
    const data = await get(`/inventory/stock-transfers?page=${currentPage}&page_size=${pageSize}`);
    if (data?.success) {
      setRowData(data.transfers.data);
      setTotalPages(data.transfers.last_page);
      setTotalRecords(data.transfers.total);
    }
    setLoading(false);
  }, [currentPage, pageSize]);

  useEffect(() => {
    getTransfers();
  }, [getTransfers]);

  const act = useCallback(async (action, id) => {
    setLoading(true);
    const data = await post(`/inventory/stock-transfers/${action}/${id}`, {});
    if (data?.success) {
      toast.success(t(`transfers.${action}Success`));
      getTransfers();
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setLoading(false);
  }, [getTransfers, t]);

  const deleteTransfer = async () => {
    const data = await del(`/inventory/stock-transfers/delete/${toDelete}`);
    if (data?.success) getTransfers();
    else toast.error(data?.message || t('common.somethingWentWrong'));
    setToDelete(null);
  };

  const columnDefs = useMemo(() => [
    { field: 'transfer_number', headerName: t('transfers.number'), flex: 1, sortable: true },
    {
      field: 'source_warehouse.name',
      headerName: t('transfers.from'),
      flex: 1,
      valueGetter: (p) => p.data?.source_warehouse?.name ?? '-'
    },
    {
      field: 'destination_warehouse.name',
      headerName: t('transfers.to'),
      flex: 1,
      valueGetter: (p) => p.data?.destination_warehouse?.name ?? '-'
    },
    { field: 'transfer_date', headerName: t('common.date'), flex: 1, sortable: true },
    {
      field: 'status',
      headerName: t('common.status'),
      flex: 1,
      sortable: true,
      cellRenderer: (p) => (
        <Badge color={STATUS_COLORS[p.value] ?? 'secondary'}>
          {t(`transfers.status.${p.value}`)}
        </Badge>
      )
    },
    { headerName: t('common.actions'), minWidth: 150, flex: 1, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer }
  ], [t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, act, setToDelete, t }), [navigate, act, t]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('transfers.title')} onReload={getTransfers} dataTotal={totalRecords}>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/inventory/stock-transfers/create')}>
            {t('transfers.newTransfer')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
            onRowClicked={(e) => {
              if (e.event?.target?.closest('button')) return;
              navigate(`/inventory/stock-transfers/view/${e.data.id}`);
            }}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <ConfirmationModal
        isOpen={toDelete !== null}
        title={t('transfers.deleteTransfer')}
        message={t('transfers.deleteTransferConfirm')}
        onConfirm={deleteTransfer}
        toggle={() => setToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default TransfersPage;
